import { MutableHighlightHandle } from './highlight-handle.ts';
import {
  type AddHighlightOptions,
  type DomHighlightsRenderer,
  type HighlightHandle,
  type HighlightsRenderer
} from './highlights-renderer.ts';
import {
  HIGHLIGHT_FIXED_FRAGMENT_OPEN_END_CLASS,
  HIGHLIGHT_SCROLLING_FRAGMENT_OPEN_START_CLASS
} from './styles/constants.ts';
import { type HighlightMode, type HighlightRange } from './types.ts';

const BASE_FRAGMENT_ID_SUFFIX = ':highlight-surface:base';
const PINNED_FRAGMENT_ID_SUFFIX = ':highlight-surface:pinned-leading';

interface PinnedLayerLease {
  readonly renderer: DomHighlightsRenderer;
  release(): void;
}

interface ProjectedFragment {
  readonly surface: 'base' | 'pinned-leading';
  readonly role: 'primary' | 'continuation';
  readonly options: AddHighlightOptions;
}

/**
 * One feature-owned logical layer backed by the table strategy's base renderer
 * and optional pinned renderer. It stores logical inputs so a layout change can
 * rebuild physical fragments without involving the feature consumer.
 */
export class ProjectedHighlightsRenderer implements HighlightsRenderer {
  #activeHighlights = new Map<
    string,
    { options: AddHighlightOptions; handle: MutableHighlightHandle }
  >();
  #pinnedHighlightIds = new Set<string>();
  #baseRenderer: DomHighlightsRenderer;
  #getPinnedColumnCount: () => number;
  #getBaseFragmentZIndex: (options: AddHighlightOptions) => number;
  #projectRowsAndContainerToPinnedSurface: boolean;
  #acquirePinnedLayer: () => PinnedLayerLease;
  #onDispose: () => void;

  #pinnedLayerLease: PinnedLayerLease | null = null;
  #hidden = false;
  #disposed = false;

  constructor({
    baseRenderer,
    getPinnedColumnCount,
    getBaseFragmentZIndex,
    projectRowsAndContainerToPinnedSurface = false,
    acquirePinnedLayer,
    onDispose
  }: {
    baseRenderer: DomHighlightsRenderer;
    getPinnedColumnCount: () => number;
    getBaseFragmentZIndex: (options: AddHighlightOptions) => number;
    projectRowsAndContainerToPinnedSurface?: boolean;
    acquirePinnedLayer: () => PinnedLayerLease;
    onDispose: () => void;
  }) {
    this.#baseRenderer = baseRenderer;
    this.#getPinnedColumnCount = getPinnedColumnCount;
    this.#getBaseFragmentZIndex = getBaseFragmentZIndex;
    this.#projectRowsAndContainerToPinnedSurface = projectRowsAndContainerToPinnedSurface;
    this.#acquirePinnedLayer = acquirePinnedLayer;
    this.#onDispose = onDispose;
  }

  add(options: AddHighlightOptions): HighlightHandle {
    if (this.#disposed) {
      throw new Error('Cannot add a highlight to a disposed renderer.');
    }

    const active = this.#activeHighlights.get(options.range.id);
    const handle = active?.handle ?? new MutableHighlightHandle(options.range.id);

    this.#activeHighlights.set(options.range.id, { options, handle });
    this.#syncHighlight(options, handle);

    return handle;
  }

  update(range: HighlightRange, mode: HighlightMode) {
    const active = this.#activeHighlights.get(range.id);

    if (!active) {
      return;
    }

    const options = { ...active.options, range, mode };

    this.#activeHighlights.set(range.id, { ...active, options });
    this.#syncHighlight(options, active.handle);
  }

  hideAll() {
    this.#hidden = true;
    this.#baseRenderer.hideAll();
    this.#pinnedLayerLease?.renderer.hideAll();
  }

  showAll() {
    this.#hidden = false;
    this.#baseRenderer.showAll();
    this.#pinnedLayerLease?.renderer.showAll();
  }

  redrawAll() {
    this.#baseRenderer.redrawAll();
    this.#pinnedLayerLease?.renderer.redrawAll();
  }

  remove(id: string) {
    const active = this.#activeHighlights.get(id);

    active?.handle.dispose();
    this.#baseRenderer.remove(this.#baseFragmentId(id));
    this.#pinnedLayerLease?.renderer.remove(this.#pinnedFragmentId(id));
    this.#pinnedHighlightIds.delete(id);
    this.#activeHighlights.delete(id);
    this.#releasePinnedLayerWhenUnused();
  }

  clear() {
    for (const { handle } of this.#activeHighlights.values()) {
      handle.dispose();
    }

    this.#baseRenderer.clear();
    this.#pinnedLayerLease?.renderer.clear();
    this.#activeHighlights.clear();
    this.#pinnedHighlightIds.clear();
    this.#releasePinnedLayer();
  }

  dispose() {
    if (this.#disposed) {
      return;
    }

    this.#disposed = true;
    this.clear();
    this.#baseRenderer.dispose();
    this.#onDispose();
  }

  reprojectAll() {
    for (const { handle } of this.#activeHighlights.values()) {
      handle.setFragments([]);
    }

    this.#baseRenderer.clear();
    this.#pinnedLayerLease?.renderer.clear();
    this.#pinnedHighlightIds.clear();

    for (const { options, handle } of this.#activeHighlights.values()) {
      this.#syncHighlight(options, handle, false);
    }

    this.#releasePinnedLayerWhenUnused();
  }

  #syncHighlight(
    options: AddHighlightOptions,
    handle: MutableHighlightHandle,
    releaseUnusedPinnedLayer = true
  ) {
    const fragments = this.#project(options);
    const baseFragment = fragments.find((fragment) => fragment.surface === 'base');
    const pinnedFragment = fragments.find(
      (fragment) => fragment.surface === 'pinned-leading'
    );
    let baseElement: HTMLElement | null = null;
    let pinnedElement: HTMLElement | null = null;

    if (baseFragment) {
      baseElement = this.#baseRenderer.addElement(baseFragment.options);
    } else {
      handle.removeFragment('base');
      this.#baseRenderer.remove(this.#baseFragmentId(options.range.id));
    }

    if (pinnedFragment) {
      this.#pinnedHighlightIds.add(options.range.id);
      pinnedElement = this.#getPinnedRenderer().addElement(pinnedFragment.options);
    } else {
      this.#pinnedHighlightIds.delete(options.range.id);
      handle.removeFragment('pinned-leading');
      this.#pinnedLayerLease?.renderer.remove(this.#pinnedFragmentId(options.range.id));
    }

    handle.setFragments(
      fragments.flatMap((fragment) => {
        const element = fragment.surface === 'base' ? baseElement : pinnedElement;

        return element
          ? [
              {
                key: fragment.surface,
                role: fragment.role,
                element
              }
            ]
          : [];
      })
    );

    if (releaseUnusedPinnedLayer) {
      this.#releasePinnedLayerWhenUnused();
    }
  }

  #project(options: AddHighlightOptions): readonly ProjectedFragment[] {
    const fixedColumnCount = this.#getPinnedColumnCount();
    const minCol = Math.min(options.range.anchor.col, options.range.lead.col);
    const maxCol = Math.max(options.range.anchor.col, options.range.lead.col);
    const canUsePinnedSurface =
      fixedColumnCount > 0 &&
      (options.mode === 'cell' || options.mode === 'column') &&
      minCol < fixedColumnCount;

    if (
      fixedColumnCount > 0 &&
      this.#projectRowsAndContainerToPinnedSurface &&
      (options.mode === 'row' || options.mode === 'container')
    ) {
      return [
        this.#baseFragment(
          options,
          options.range,
          this.#joinClasses(
            options.className,
            HIGHLIGHT_SCROLLING_FRAGMENT_OPEN_START_CLASS
          ),
          'primary'
        ),
        this.#pinnedFragment(
          options,
          options.range,
          this.#joinClasses(options.className, HIGHLIGHT_FIXED_FRAGMENT_OPEN_END_CLASS),
          'continuation'
        )
      ];
    }

    if (!canUsePinnedSurface) {
      return [this.#baseFragment(options, options.range, options.className, 'primary')];
    }

    if (maxCol < fixedColumnCount) {
      return [this.#pinnedFragment(options, options.range, options.className, 'primary')];
    }

    const scrollingRange: HighlightRange = {
      ...options.range,
      anchor: {
        ...options.range.anchor,
        col: Math.max(options.range.anchor.col, fixedColumnCount)
      },
      lead: {
        ...options.range.lead,
        col: Math.max(options.range.lead.col, fixedColumnCount)
      }
    };
    const pinnedRange: HighlightRange = {
      ...options.range,
      anchor: {
        ...options.range.anchor,
        col: Math.min(options.range.anchor.col, fixedColumnCount - 1)
      },
      lead: {
        ...options.range.lead,
        col: Math.min(options.range.lead.col, fixedColumnCount - 1)
      }
    };

    return [
      this.#baseFragment(
        options,
        scrollingRange,
        this.#joinClasses(
          options.className,
          HIGHLIGHT_SCROLLING_FRAGMENT_OPEN_START_CLASS
        ),
        'primary'
      ),
      this.#pinnedFragment(
        options,
        pinnedRange,
        this.#joinClasses(options.className, HIGHLIGHT_FIXED_FRAGMENT_OPEN_END_CLASS),
        'continuation'
      )
    ];
  }

  #baseFragment(
    options: AddHighlightOptions,
    range: HighlightRange,
    className: string | undefined,
    role: ProjectedFragment['role']
  ): ProjectedFragment {
    return {
      surface: 'base',
      role,
      options: {
        ...options,
        range: { ...range, id: this.#baseFragmentId(options.range.id) },
        zIndex: this.#getBaseFragmentZIndex(options),
        className,
        elementId: role === 'primary' ? options.elementId : undefined
      }
    };
  }

  #pinnedFragment(
    options: AddHighlightOptions,
    range: HighlightRange,
    className: string | undefined,
    role: ProjectedFragment['role']
  ): ProjectedFragment {
    return {
      surface: 'pinned-leading',
      role,
      options: {
        ...options,
        range: { ...range, id: this.#pinnedFragmentId(options.range.id) },
        className,
        elementId: role === 'primary' ? options.elementId : undefined
      }
    };
  }

  #getPinnedRenderer(): DomHighlightsRenderer {
    if (!this.#pinnedLayerLease) {
      this.#pinnedLayerLease = this.#acquirePinnedLayer();

      if (this.#hidden) {
        this.#pinnedLayerLease.renderer.hideAll();
      }
    }

    return this.#pinnedLayerLease.renderer;
  }

  #releasePinnedLayerWhenUnused() {
    if (this.#pinnedHighlightIds.size > 0) {
      return;
    }

    this.#releasePinnedLayer();
  }

  #releasePinnedLayer() {
    this.#pinnedLayerLease?.release();
    this.#pinnedLayerLease = null;
  }

  #baseFragmentId(id: string): string {
    return `${id}${BASE_FRAGMENT_ID_SUFFIX}`;
  }

  #pinnedFragmentId(id: string): string {
    return `${id}${PINNED_FRAGMENT_ID_SUFFIX}`;
  }

  #joinClasses(...classNames: Array<string | undefined>): string {
    return classNames.filter(Boolean).join(' ');
  }
}
